import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";

// Lazy load route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AccountSetup = lazy(() => import("./pages/AccountSetup"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Blog = lazy(() => import("./pages/Blog"));
const BuildSystemsArticle = lazy(() => import("./pages/blog/BuildSystemsArticle"));
const BottlenecksArticle = lazy(() => import("./pages/blog/BottlenecksArticle"));
const HireConsultantArticle = lazy(() => import("./pages/blog/HireConsultantArticle"));
const ClientFulfillmentArticle = lazy(() => import("./pages/blog/ClientFulfillmentArticle"));
const FounderCEOArticle = lazy(() => import("./pages/blog/FounderCEOArticle"));
const ClientOnboarding = lazy(() => import("./pages/client/ClientOnboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Client Portal
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const EngagementDetail = lazy(() => import("./pages/client/EngagementDetail"));
const ClientMessages = lazy(() => import("./pages/client/ClientMessages"));
const ClientBilling = lazy(() => import("./pages/client/ClientBilling"));
const ClientNotifications = lazy(() => import("./pages/client/ClientNotifications"));
const ClientProgress = lazy(() => import("./pages/client/ClientProgress"));
const ClientResources = lazy(() => import("./pages/client/ClientResources"));

// Admin Portal
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminResources = lazy(() => import("./pages/admin/AdminResources"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));
const AdminSubmissions = lazy(() => import("./pages/admin/AdminSubmissions"));

const AdminDeals = lazy(() => import("./pages/admin/AdminDeals"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const ProjectDetail = lazy(() => import("./pages/admin/ProjectDetail"));
const AdminScheduling = lazy(() => import("./pages/admin/AdminScheduling"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ErrorBoundary>
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div></div>}>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/account-setup" element={<AccountSetup />} />
              <Route path="/portal/invite" element={<Navigate to="/account-setup" replace />} />
              <Route path="/client/login" element={<Navigate to="/login" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/build-systems-remove-daily-operations" element={<BuildSystemsArticle />} />
              <Route path="/blog/bottlenecks-kill-small-business-growth" element={<BottlenecksArticle />} />
              <Route path="/blog/when-to-hire-business-consultant" element={<HireConsultantArticle />} />
              <Route path="/blog/fix-client-fulfillment-process-scale" element={<ClientFulfillmentArticle />} />
              <Route path="/blog/founder-to-ceo-transition" element={<FounderCEOArticle />} />
              
              {/* Redirects for removed suite pages */}
              <Route path="/flourish-suite" element={<Navigate to="/" replace />} />
              <Route path="/foundation-suite" element={<Navigate to="/" replace />} />
              <Route path="/framework-suite" element={<Navigate to="/" replace />} />
              <Route path="/onboarding" element={<ProtectedRoute><ClientOnboarding /></ProtectedRoute>} />
              
              {/* Client Portal Routes */}
              <Route path="/client/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
              <Route path="/client/progress" element={<ProtectedRoute><ClientProgress /></ProtectedRoute>} />
              <Route path="/client/engagement/:id" element={<ProtectedRoute><EngagementDetail /></ProtectedRoute>} />
              <Route path="/client/messages" element={<ProtectedRoute><ClientMessages /></ProtectedRoute>} />
              <Route path="/client/billing" element={<ProtectedRoute><ClientBilling /></ProtectedRoute>} />
              <Route path="/client/resources" element={<ProtectedRoute><ClientResources /></ProtectedRoute>} />
              <Route path="/client/notifications" element={<ProtectedRoute><ClientNotifications /></ProtectedRoute>} />
              
              {/* Admin Portal Routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/marketing" element={<ProtectedRoute requireAdmin><AdminMarketing /></ProtectedRoute>} />
              <Route path="/admin/deals" element={<ProtectedRoute requireAdmin><AdminDeals /></ProtectedRoute>} />
              <Route path="/admin/submissions" element={<ProtectedRoute requireAdmin><AdminSubmissions /></ProtectedRoute>} />
              <Route path="/admin/clients" element={<ProtectedRoute requireAdmin><AdminClients /></ProtectedRoute>} />
              <Route path="/admin/engagements" element={<Navigate to="/admin/clients" replace />} />
              <Route path="/admin/messages" element={<ProtectedRoute requireAdmin><AdminMessages /></ProtectedRoute>} />
              <Route path="/admin/invoices" element={<ProtectedRoute requireAdmin><AdminInvoices /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/security" element={<ProtectedRoute requireAdmin><AdminSecurity /></ProtectedRoute>} />
              <Route path="/admin/resources" element={<ProtectedRoute requireAdmin><AdminResources /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute requireAdmin><AdminNotifications /></ProtectedRoute>} />
              
              <Route path="/admin/projects" element={<ProtectedRoute requireAdmin><AdminProjects /></ProtectedRoute>} />
              <Route path="/admin/projects/:id" element={<ProtectedRoute requireAdmin><ProjectDetail /></ProtectedRoute>} />
              <Route path="/admin/scheduling" element={<ProtectedRoute requireAdmin><AdminScheduling /></ProtectedRoute>} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
